import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Button from "../Button/Button";
import DropDownVariantIconButton from "../DropDown/DropDownVariantIconButton";

import { useRemToPx } from "@/shared/hooks/viewMode";
import EgwWebFont from "@/shared/assets/EgwWebFont";

import { handleKeyDownNavigation } from "../../utils/keyboard-navigation/keyboard-navigation";
import {
  filterAlphabetThreeDotsButtonClassName,
  filterAlphabetThreeDotsPopupClassName,
} from "./FilterAlphabet.constants";

import styles from "./FilterAlphabet.module.scss";

const FilterAlphabet = ({ isHorizontal, onFilter, filter, chars, className }) => {
  const remToPx = useRemToPx();
  const [elRoot, setElRoot] = useState(null);
  const refHiddenContent = useRef();

  const sizeAvailable = elRoot
    ? isHorizontal
      ? elRoot.offsetWidth
      : elRoot.offsetHeight
    : undefined;

  const list = chars.map((item, index) => {
    return (
      <Button
        key={index}
        tabIndex={index === 0 ? 0 : -1}
        variant={Button.variant.clear}
        color={Button.color.icon}
        size={Button.size.big}
        active={filter === item}
        onClick={() => {
          onFilter(item === filter ? null : item);
        }}
      >
        {item}
      </Button>
    );
  });

  const charHeight = isHorizontal ? remToPx(1.28) : remToPx(1.52);
  const threeDotsBtnHeight = remToPx(1.2);

  const { availableList, hiddenList } = list.reduce(
    (acc, item) => {
      acc.listHeight += charHeight;

      if (acc.listHeight > sizeAvailable) {
        acc.hiddenList.push(item);
      } else {
        acc.availableList.push(item);
      }

      return acc;
    },
    {
      availableList: [],
      hiddenList: [],
      listHeight: threeDotsBtnHeight,
    }
  );

  const handleKeyDownRoot = (e) => handleKeyDownNavigation(elRoot, e, true);

  const handleHiddenContentKeyDown = (e) => {
    handleKeyDownNavigation(refHiddenContent.current?.view, e, true);
  };

  return (
    <div
      ref={setElRoot}
      className={classNames(
        styles["root"],
        className,
        isHorizontal ? styles["horizontal"] : styles["vertical"]
      )}
      onKeyDown={handleKeyDownRoot}
    >
      <div className={styles["inner"]}>
        {availableList}

        {!!hiddenList.length && (
          <DropDownVariantIconButton
            className={filterAlphabetThreeDotsButtonClassName}
            icon={EgwWebFont["dots-vertical"]}
            popupProps={{
              className: filterAlphabetThreeDotsPopupClassName,
              contentProps: {
                ref: refHiddenContent,
                renderView: (props) => {
                  return (
                    <div
                      {...props}
                      className={classNames(props.className, styles["popup-inner"])}
                      style={{
                        ...props.style,
                        maxWidth: sizeAvailable - threeDotsBtnHeight,
                      }}
                      onKeyDown={handleHiddenContentKeyDown}
                    />
                  );
                },
              },
            }}
          >
            {hiddenList}
          </DropDownVariantIconButton>
        )}
      </div>
    </div>
  );
};

FilterAlphabet.propTypes = {
  isHorizontal: PropTypes.bool,
  chars: PropTypes.array,
  filter: PropTypes.string,
  onFilter: PropTypes.func,
  className: PropTypes.string,
};

export default FilterAlphabet;
